/*
 * @author Andrey Patyuchenko
 * @email  apatyuchenko@atlassian.com
 * @email  andrey.patyuchenko@gmail.com
 * @date   4/26/22
 */

import React from "react";
import {Button, FormControl, IconButton, MenuItem, Select, Stack, Tooltip} from "@mui/material";
import {
	EventsFilter, EventsFilterAttribute,
	EventsFilterItem,
	EventsFilterOperation
} from "../../interfaces/Global";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EventTypeControl from "../../controls/EventTypeControl";
import BooleanControl from "../../controls/BooleanControl";

export interface ConfigEventsFilterProps {
	filter: EventsFilter
	setFilter: (filter: EventsFilter) => void
	disabled?: boolean
}

export default function ConfigEventsFilter(props: ConfigEventsFilterProps) {

	return (
		<Stack spacing={2}>
			{props.filter.length === 0 && <Stack />}
			{props.filter.map((item, index) =>
				<FilterItem
					key={index + item[0] + item[1]}
					item={item}
					set={(item) => {
						const newFilter = [ ...props.filter ];
						newFilter[index] = item;
						props.setFilter(newFilter);
					}}
					remove={() => {
						props.setFilter(props.filter.filter((item, i) => i !== index));
					}}
					disabled={props.disabled}
				/>)
			}
			<Stack direction={"row"}>
				<Button
					variant="outlined"
					size={"small"}
					startIcon={<AddIcon />}
					disabled={props.disabled}
					onClick={() => {
						props.setFilter([
							...props.filter,
							["and" as EventsFilterOperation, "typeId" as EventsFilterAttribute, []]
						]);
					}}
				>
					Add condition
				</Button>
			</Stack>
		</Stack>
	);

}

export interface FilterItemProps {
	item: EventsFilterItem,
	set: (item: EventsFilterItem) => void,
	remove: () => void
	disabled?: boolean
}

function FilterItem({ item, set, remove, disabled }: FilterItemProps) {

	const [op, attribute, values] = item;

	const submit = (o: EventsFilterOperation, a: EventsFilterAttribute, v: string[]) => { set([o, a, v]) };

	return (
		<Stack direction={"row"} spacing={3} alignItems={"flex-start"}>
			<FilterItemAttributeSelector
				disabled={disabled}
				attribute={attribute}
				set={(attribute) => submit(op, attribute, [])}
			/>
			<FilterItemValuesSelector
				disabled={disabled}
				attribute={attribute}
				values={values}
				set={(values) => submit(op, attribute, values)}
			/>
			<IconButton disabled={disabled} onClick={remove} size={"small"}>
				<Tooltip title={"Remove Condition"}>
					<CloseIcon />
				</Tooltip>
			</IconButton>
		</Stack>
	)
}

interface FilterItemAttributeSelectorProps {
	attribute: EventsFilterAttribute
	set: (attribute: EventsFilterAttribute) => void
	disabled?: boolean
}

function FilterItemAttributeSelector({ attribute, set, disabled }: FilterItemAttributeSelectorProps) {

	return (
		<FormControl variant={"standard"}>
			<Select
				value={attribute}
				disabled={disabled}
				onChange={event => {
					set(event.target.value as EventsFilterAttribute);
				}}
				sx={{ width: "200px" }}
			>
				<MenuItem value={"typeId"}>Type</MenuItem>
				<MenuItem value={"categoryId"}>Category</MenuItem>
				<MenuItem value={"isConfirmed"}>Confirmed</MenuItem>
			</Select>
		</FormControl>
	);
}

interface FilterItemValuesSelectorProps {
	attribute: EventsFilterAttribute, values: string[]
	set: (values: string[]) => void
	disabled?: boolean
}

function FilterItemValuesSelector({ attribute, values, set, disabled }: FilterItemValuesSelectorProps) {

	return (
		<FormControl variant={"standard"} sx={{ flexGrow: 2 }}>
			{attribute === "typeId" &&
			<EventTypeControl
				showTypes={true}
				showCategories={false}
				values={values}
				onChange={(values) => set(values)}
				disabled={disabled}
			/>}
			{attribute === "categoryId" &&
			<EventTypeControl
				showTypes={false}
				showCategories={true}
				values={values}
				onChange={(values) => set(values)}
				disabled={disabled}
			/>}
			{attribute === "isConfirmed" &&
			<BooleanControl
				value={values.length === 0 ? undefined : values.includes("true")}
				onChange={(value) => set(value ? ["true"] : ["false"])}
				hideNoFilter={true}
				disabled={disabled}
			/>}
		</FormControl>
	);
}